import { Capacitor } from '@capacitor/core';
import { isSessionChangedError, isSessionEndedError } from '../gql/refreshFailure';

/**
 * Concurrencia de subida.
 *
 * `navigator.connection` funciona en Chrome Y en el WebView de Android (los dos
 * son Chromium), pero la propia especificación dice que estima conectividad
 * PASADA, no la velocidad actual. Se usa como pista para arrancar, nunca como
 * verdad: quien manda es lo que midan los bytes reales.
 *
 * El consenso del sector son 3-5 simultáneas. Por encima se satura el enlace
 * sin ganar nada y, en uno estrecho, es peor que ir en serie: se reparten el
 * ancho de banda y ninguna termina hasta el final.
 */
export function resolveConcurrency(): number {
  const effectiveType = (
    navigator as Navigator & { connection?: { effectiveType?: string } }
  ).connection?.effectiveType;

  if (effectiveType === 'slow-2g' || effectiveType === '2g') return 1;
  if (effectiveType === '3g') return 2;

  const isHandheld =
    Capacitor.isNativePlatform() ||
    (typeof window !== 'undefined' &&
      window.matchMedia?.('(pointer: coarse)').matches === true);

  return isHandheld ? 3 : 6;
}

/**
 * 4xx = el servidor rechaza ESTA petición concreta (token caducado, petición
 * malformada). Reintentar no arregla nada y solo gasta batería y datos.
 */
function isTerminal(error: unknown): boolean {
  // Con la sesión terminada o cambiada, reintentar saldría sin token o con el
  // de otro (spec § 12.4).
  if (isSessionEndedError(error) || isSessionChangedError(error)) return true;
  // Sin servidor (spec 2026-10-09 § 9): `mutateWithDeadline` ni la envió, y en
  // 1-4 s saldría igual. La pasada se para y se retoma cuando vuelva.
  if ((error as { name?: string } | undefined)?.name === 'BackendUnreachableError') return true;
  const status = (error as { httpStatus?: number } | undefined)?.httpStatus;
  return typeof status === 'number' && status >= 400 && status < 500;
}

const defaultBackoff = (attempt: number): number => {
  const base = [1000, 4000, 12000][attempt] ?? 12000;
  // Jitter: si 20 fotos fallan a la vez por un corte, sin él reintentarían
  // todas en el mismo milisegundo y volverían a tumbarse entre ellas.
  return base + Math.floor(Math.random() * 1000);
};

export interface WithRetryOptions {
  /** Intentos TOTALES. 0 o 1 = un intento, sin reintento. */
  retries?: number;
  backoffMs?: (attempt: number) => number;
}

/**
 * Reintenta `fn` ante errores transitorios, con retroceso.
 *
 * Vive aquí y no dentro del pool para que el sync manager pueda reutilizar
 * exactamente la misma política sin depender del pool: allí la contabilidad de
 * reintentos ENTRE sesiones ya la lleva la cola (`retryCount`, MAX_RETRIES), y
 * lo que falta es el reintento DENTRO de una misma pasada — sin él, con
 * cobertura intermitente cada corte deja la foto esperando a un evento de
 * reconexión que puede no llegar nunca.
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  { retries = 3, backoffMs = defaultBackoff }: WithRetryOptions = {},
): Promise<T> {
  let attempt = 0;

  for (;;) {
    try {
      return await fn();
    } catch (error) {
      const noMoreAttempts = retries <= 1 || attempt >= retries - 1;
      if (isTerminal(error) || noMoreAttempts) throw error;
      await new Promise((r) => setTimeout(r, backoffMs(attempt)));
      attempt += 1;
    }
  }
}

export interface UploadPoolOptions {
  concurrency: number;
  /** Intentos TOTALES por elemento. 0 = un intento, sin reintento. */
  retries?: number;
  backoffMs?: (attempt: number) => number;
  onSettled?: (index: number, ok: boolean) => void;
}

export interface UploadPoolResult {
  ok: number[];
  failed: Array<{ index: number; error: unknown }>;
}

/**
 * Ejecuta `worker` sobre `items` con concurrencia acotada y reintento por
 * elemento.
 *
 * NUNCA rechaza en bloque: devuelve qué terminó y qué no. Esa es la diferencia
 * de fondo con el `Promise.all` que sustituye — allí una sola foto rota
 * abortaba el guardado completo de la actuación.
 */
export async function runUploadPool<T>(
  items: T[],
  worker: (item: T, index: number) => Promise<void>,
  {
    concurrency,
    retries = 3,
    backoffMs = defaultBackoff,
    onSettled,
  }: UploadPoolOptions,
): Promise<UploadPoolResult> {
  const result: UploadPoolResult = { ok: [], failed: [] };
  if (items.length === 0) return result;

  let cursor = 0;

  const runOne = async (index: number): Promise<void> => {
    try {
      await withRetry(() => worker(items[index], index), {
        retries,
        backoffMs,
      });
      result.ok.push(index);
      onSettled?.(index, true);
    } catch (error) {
      result.failed.push({ index, error });
      onSettled?.(index, false);
    }
  };

  const runner = async (): Promise<void> => {
    for (;;) {
      const index = cursor;
      cursor += 1;
      if (index >= items.length) return;
      await runOne(index);
    }
  };

  await Promise.all(
    Array.from({ length: Math.max(1, Math.min(concurrency, items.length)) }, () =>
      runner(),
    ),
  );

  result.ok.sort((a, b) => a - b);
  result.failed.sort((a, b) => a.index - b.index);
  return result;
}
